import React, { useState } from "react";
import { RCCAlert, RCCButton, RCCGrid, RCCHidden, RCCHookToast, RCCServicesApi } from "react-common-components";
import Accordion from "src/components/Accordion";
import Loader from "src/components/Loader";
import Title from "src/components/Title";
import { useTranslation } from "src/context/Translations/context";
import { standardErrorMessages } from "src/utils/errors";
import { CheckQuadrant, RisksSituation, IGrouperOfRisks, IRisks, GroupersRisks } from "../interface";
import { FilterButton, FilterButtonSelected } from "../styles";
import ListOfRisks from "./listOfRisks";

const GrouperOfRisks: React.FC<IGrouperOfRisks> = ({
	grouper,
	filters,
	groupersSelect,
	layoutList
}) => {
	const {translate} = useTranslation();
	const {addToast} = RCCHookToast();

	const [list, setList] = useState<IRisks[]>([]);
	const [loading, setLoading] = useState<boolean>(true);

	const [filterListSituation, setFilterListSituation] = useState<RisksSituation | undefined>(undefined);
	const [filterListQuadrant, setFilterListQuadrant] = useState<CheckQuadrant | undefined>(undefined);

	const handleClickGroupers = async (_e: any, expanded: boolean, grouperId: number) => {
		if(!expanded)
			return;
		try{
			const {data} = await RCCServicesApi.getRequest({
				method: "Risks/List",
				urlParameters: {
					...filters,
					...groupersSelect,
					grouperId: grouperId > 0 ? grouperId.toString() : "",
					situation: filters.situation && objToValue(filters.situation.toString()) !== 4  ? filters.situation : ""
				}
			});
			const listRisks = data as IRisks[];
			setList(listRisks);
			setLoading(false);	
		}catch(err){
			standardErrorMessages(err, translate("Erro ao carregar os riscos"), addToast);
			setLoading(false);	
		}
	};

	const objToValue = (obj: any) => {
		if(typeof obj === "object")
			return obj.value;
		if(typeof obj === "string")
			return parseInt(obj, 10) ;
		return obj;
	};

	const onCallbackDelete = (rib001Id: number) => {
		setList((prevState) => prevState.filter((x) => x.rib001Id !== rib001Id));
	};

	const onCallbackUpdate = async (id: number) => {
		try{
			const response = await RCCServicesApi.getRequest({
				method: "Risks/InfosOfRiskForList",
				urlParameters: {
					id
				}
			});
			const risk = response.data as IRisks;

			setList((prevState) => prevState.map((glist) => {
				if(glist.rib001Id === id)
					return risk;
				return glist;
			}));
		}catch(err){
			standardErrorMessages(err, translate("Erro ao atualizar risco, favor atualizar a pagina"), addToast);
		}
	};

	const filterQuadrantRisksOnClickCountBar = (quadrant: CheckQuadrant) => {
		let quadrantToUpdate: CheckQuadrant | undefined = quadrant;
		
		if(filterListQuadrant && filterListQuadrant === quadrant)
			quadrantToUpdate = undefined;
		
		setFilterListQuadrant(quadrantToUpdate);
	};

	const filterSituationRisksOnClickCountBar = (situation: RisksSituation) => {
		let situationToUpdate: RisksSituation | undefined = situation;
		
		if(filterListSituation && filterListSituation === situation)
			situationToUpdate = undefined;
		
		setFilterListSituation(situationToUpdate);
	};

	const groupTitle = (text: string) => {
		switch (groupersSelect.group) {
		case GroupersRisks.RISK_SECTOR:
			return text === "" ? translate("Sem setor de risco") : text;
		case GroupersRisks.IMPACT_SECTOR:
			return text === "" ? translate("Sem setor de impacto") : text;
		default:
			return text;
		}
	};

	return (
		<Accordion 
			header={
				<>
					<RCCGrid 
						container
						item 
						alignItems="center"
						justifyContent="space-between"
						style={{  position: "relative" }}
					>
						<div 
							style={{ display: "flex", alignItems: "center", flex: 1, width: "auto" }}
						>
							<span>{groupTitle(grouper.title)}</span>				
						</div>
						<div style={{ display: "flex"}}>
							<div style={{ display: "flex", flexDirection: "column"}}>
								<RCCGrid container justifyContent="flex-end">
									{grouper.opportunity > 0 && (
										<RCCButton 
											color="purple" 
											styleInline={filterListQuadrant && filterListQuadrant !== CheckQuadrant.OPPORTUNITY ? FilterButtonSelected : FilterButton}
											onClick={(e) => { e.stopPropagation(); filterQuadrantRisksOnClickCountBar(CheckQuadrant.OPPORTUNITY); }}
										>
											{grouper.opportunity} <RCCHidden only="xs">{grouper.opportunity > 1 ? translate("oportunidades") : translate("oportunidade")}</RCCHidden>
										</RCCButton>
									)}
									{grouper.threat > 0 && (
										<RCCButton 
											color="red" 
											styleInline={filterListQuadrant && filterListQuadrant !== CheckQuadrant.THREAT ? FilterButtonSelected : FilterButton}
											onClick={(e) => { e.stopPropagation(); filterQuadrantRisksOnClickCountBar(CheckQuadrant.THREAT); }}
										>
											{grouper.threat} <RCCHidden only="xs">{grouper.threat > 1 ? translate("ameaças") : translate("ameaça")}</RCCHidden> 
										</RCCButton>
									)}
								</RCCGrid>
								<RCCGrid container justifyContent="flex-end">
									{grouper.finished > 0 && (
										<RCCButton 
											color="blue" 
											styleInline={filterListSituation && filterListSituation !== RisksSituation.FINALY ? FilterButtonSelected : FilterButton}
											onClick={(e) => { e.stopPropagation(); filterSituationRisksOnClickCountBar(RisksSituation.FINALY); }}
										>
											{grouper.finished} <RCCHidden only="xs">{grouper.finished > 1 ? translate("finalizados") : translate("finalizado")}</RCCHidden> 
										</RCCButton>
									)}
									{grouper.opened > 0 && (
										<RCCButton 
											color="green" 
											styleInline={filterListSituation && filterListSituation !== RisksSituation.OPEN ? FilterButtonSelected : FilterButton}
											onClick={(e) => { e.stopPropagation(); filterSituationRisksOnClickCountBar(RisksSituation.OPEN); }}
										>{grouper.opened} <RCCHidden only="xs">{grouper.opened > 1 ? translate("abertos") : translate("aberto")}</RCCHidden> 
										</RCCButton>
									)}
									{grouper.canceled > 0  && (
										<RCCButton 
											color="black" 
											styleInline={filterListSituation && filterListSituation !== RisksSituation.CANCELED ? FilterButtonSelected : FilterButton}
											onClick={(e) => { e.stopPropagation(); filterSituationRisksOnClickCountBar(RisksSituation.CANCELED); }}
										>{grouper.canceled} <RCCHidden only="xs"> {grouper.canceled > 1 ? translate("cancelados") : translate("cancelado")}</RCCHidden>
										</RCCButton>
									)}
								</RCCGrid>
							</div>
							<div style={{ width: "120px", padding: "0 20px",  alignSelf: "center"}}>
								<Title color="purple" size="sm" styleInline="margin 0 4px;">{translate("Total")}: {grouper.count}</Title>
							</div>
							
						</div>
				
					</RCCGrid>
				</>
			}
			onChange={(e, expanded) => handleClickGroupers(e, expanded, grouper.id)}
		>
			{loading ? (
				<Loader />
			): (
				<>
					{list ? (
						<>
							<ListOfRisks 
								data={list}
								layoutList={layoutList}
								onCallbackDelete={onCallbackDelete}
								onCallbackUpdate={onCallbackUpdate}
								filterListSituation={filterListSituation}
								filterListQuadrant={filterListQuadrant}

							/>
						</>     
					): (
						<RCCAlert 
							title={translate("Atenção")}
							description={translate("Não foram encontrados dados")}
						/>
					)}
				</>
			)}
		</Accordion>
	);
};

export default GrouperOfRisks;
